import type React from 'react';
import { type Tag } from '@/lib/types';
import { IconButton } from '@mui/material';
import {
  Edit as EditIcon,
  Clear as CancelIcon,
  Check as SaveIcon,
} from '@mui/icons-material';
import { useContext, useState } from 'react';
import InputWithEscListener from '@/components/InputWithEscListener';
import useComplexSwrState from '@/lib/utils/useComplexSwrState';
import useApi from '@/lib/api/useApi';
import TagsContext from '../TagsContext';
import TagColorPicker from './TagColorPicker';
import DeleteTag from './DeleteTag';
import TagWrapper from './TagWrapper';
import PrimaryControlsWrapper from './PrimaryControlsWrapper';
import NameEditorWrapper from './NameEditorWrapper';
import TagInfoEditor from './TagInfoEditor';
import useRandomizedId from '@/lib/utils/useRandomizedId';
import { FormattedMessage, useIntl } from 'react-intl';

type TagWithControlsProps = {
  tag: Tag;
};

const TagWithControls = ({ tag }: TagWithControlsProps) => {
  const api = useApi();

  const [, setTags] = useContext(TagsContext);

  const [nameEditing, setNameEditing] = useState(false);
  const [name, setName, nameHasChanged, reenableNameSync] = useComplexSwrState(
    tag.name,
  );
  const [saving, setSaving] = useState(false);

  const saveNameChange = async (
    e: React.SubmitEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    setSaving(true);

    const updatedTag = await api.put<Tag>(`editor/tags/${tag.id}/name`, {
      name,
    });

    setTags(tags => {
      tags[tag.id] = {
        ...tags[tag.id],
        ...updatedTag,
      };
    });

    setNameEditing(false);
    setSaving(false);
    reenableNameSync();
  };

  const cancelNameChange = (): void => {
    setNameEditing(false);

    if (nameHasChanged) {
      setName(tag.name);
      reenableNameSync();
    }
  };

  const inputId = useRandomizedId('tag-name-input');

  const intl = useIntl();

  return (
    <TagWrapper tagColor={tag.color}>
      {!nameEditing ? (
        <>
          <PrimaryControlsWrapper className="primary-controls-wrapper">
            <span>{tag.name}</span>
            <IconButton size="small" onClick={() => setNameEditing(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <TagInfoEditor tag={tag} />
          </PrimaryControlsWrapper>
          <TagColorPicker tag={tag} />
          <DeleteTag tag={tag} />
        </>
      ) : (
        <NameEditorWrapper onSubmit={saveNameChange}>
          <label htmlFor={inputId} className="screenreader-only">
            <FormattedMessage id="editor.model_editor.field_editor.tags_editor.dialog.tags_manager.tag.name_input.label" />
          </label>
          <InputWithEscListener
            id={inputId}
            placeholder={intl.formatMessage({
              id: 'editor.model_editor.field_editor.tags_editor.dialog.tags_manager.tag.name_input.placeholder',
            })}
            value={name}
            onChange={e => setName(e.target.value)}
            onEscPress={cancelNameChange}
            disabled={saving}
            autoFocus
          />
          {nameHasChanged && (
            <IconButton
              size="small"
              onClick={cancelNameChange}
              disabled={saving}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            type="submit"
            size="small"
            disabled={name.trim().length === 0 || saving}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </NameEditorWrapper>
      )}
    </TagWrapper>
  );
};

export default TagWithControls;
